import { FullSearchResponseData, SearchContentSection } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { SearchFilterButton, SearchFilterDrawer, Tab, Tabs } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks';
import { List, ListItemButton, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { setModalState } from '@store/modal/modal.slice';
import {
  getSearchSection,
  getTotalCountInSections,
  setProductsCategoryLink,
  setSearchSection,
} from '@store/search/search.slice';
import { t } from '@translations';
import { ReactNode, useCallback, useMemo } from 'react';

// STYLES
//--------

/**
 * Properties
 */
export type SearchResultsTabsProps = {
  results?: FullSearchResponseData;
  isWorking?: boolean;
};

export type TabItem = {
  id: SearchContentSection[];
  label: string;
};

/**
 * Search Result Tabs
 */
export const SearchResultTabs = ({ isWorking }: SearchResultsTabsProps) => {
  const totalCountInSections = useAppSelector(getTotalCountInSections);
  const { articles = 0, documents = 0, products = 0 } = totalCountInSections;

  // Tab Config & Labels
  const getLabel = useCallback(
    (code: string, count: number) => t(code, { totalCount: count?.toString() }),
    []
  );
  const tabConfig: TabItem[] = useMemo(() => {
    return [
      {
        id: ['products', 'accessories'],
        label: getLabel(TextCodes.Search.ProductsTab, products),
      },
      {
        id: ['articles'],
        label: getLabel(TextCodes.Search.ArticlesTab, articles),
      },
      {
        id: ['documents'],
        label: getLabel(TextCodes.Search.DocumentTab, documents),
      },
    ] as TabItem[];
  }, [products, articles, documents, getLabel]);

  // Active tab
  const dispatch = useAppDispatch();
  const currentTabValue = useAppSelector(getSearchSection);

  const setSection = (id: SearchContentSection) => dispatch(setSearchSection(id));

  const changeHandler = (_: React.SyntheticEvent, newValue: SearchContentSection) => {
    dispatch(setProductsCategoryLink());
    setSection(newValue);
  };

  // Normal / mobile version
  const theme = useTheme();
  const isBreakpointMedium = useMediaQuery(theme.breakpoints.up('md'));

  // Mobile version
  const selectedSection = tabConfig.find((x) => x.id.includes(currentTabValue))?.label || '';

  const mobileTitle = t(TextCodes.Search.SelectedSection, { selectedSection });
  const onTabClick = (tab: TabItem) => {
    dispatch(setProductsCategoryLink());
    setSection(tab.id[0]);
  };
  return isBreakpointMedium ? (
    <Tabs
      variant='standard'
      onChange={changeHandler}
      value={tabConfig.find((x) => x.id.includes(currentTabValue))?.id[0] || tabConfig[0].id[0]}
      scrollButtons='auto'
      centered
    >
      {tabConfig.map((tab, key) => (
        <Tab label={tab.label} value={tab.id[0]} key={key} />
      ))}
    </Tabs>
  ) : (
    <MobileSectionSelector title={mobileTitle} isWorking={isWorking}>
      <List>
        {tabConfig.map((tab, key) => (
          <ListItemButton key={key} selected={tab.id.includes(currentTabValue)}>
            <ListItemText primary={tab.label} onClick={() => onTabClick(tab)} />
          </ListItemButton>
        ))}
      </List>
    </MobileSectionSelector>
  );
};

export type MobileSectionSelectorProps = {
  title?: string;
  isWorking?: boolean;
  children?: ReactNode;
  modalId?: string;
};

export const MobileSectionSelector = ({
  title,
  isWorking,
  children,
  modalId = 'search-section-selector',
}: MobileSectionSelectorProps) => {
  const dispatch = useAppDispatch();
  const openMobileModal = () => dispatch(setModalState({ id: modalId, isVisible: true }));
  return (
    <>
      <SearchFilterButton onClick={openMobileModal} title={title} isWorking={isWorking} />
      <SearchFilterDrawer modalId={modalId}>{children}</SearchFilterDrawer>
    </>
  );
};
